import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ComboBox, DetailsList, Spinner, SpinnerSize, IconButton } from '@fluentui/react'
import { debounce } from 'lodash'

import {
  getTournamentMatches,
  getEvents,
  getEditions,
  getRounds,
} from 'actions/tournament'
import {
  selectTournamentMatches,
  selectTournamentLoading,
  selectEvents,
  selectEditions,
  selectRounds,
} from 'selectors/tournament'

import './Tournament.scss'

const DEFAULT_EVENT = 'CTM Masters'

const matchColumns = [
  {
    key: 'matchNumber',
    name: '#',
    minWidth: 25,
    maxWidth: 75,
    fieldName: 'match_number',
  },
  {
    key: 'roundGame',
    name: 'Round/Game',
    minWidth: 100,
    maxWidth: 140,
    fieldName: 'round_game',
  },
  {
    key: 'date',
    name: 'Date',
    minWidth: 140,
    maxWidth: 150,
    onRender: (item) =>
      item.date_time ? new Date(item.date_time).toLocaleDateString() : '—',
  },
  {
    key: 'player1',
    name: 'Player 1',
    minWidth: 150,
    maxWidth: 180,
    onRender: (item) => {
      const p1 = item?.players[0]
      const p2 = item?.players[1]
      const isWinner = p1?.score > p2?.score
      return <span className={isWinner ? 'winningPlayer' : ''}>{p1?.name}</span>
    },
  },
  {
    key: 'player1Score',
    name: 'Score',
    minWidth: 50,
    maxWidth: 50,
    onRender: (item) => <span>{item?.players[0]?.score}</span>,
  },
  {
    key: 'player2',
    name: 'Player 2',
    minWidth: 150,
    maxWidth: 180,
    onRender: (item) => {
      const p1 = item?.players[0]
      const p2 = item?.players[1]
      const isWinner = p2?.score > p1?.score
      return <span className={isWinner ? 'winningPlayer' : ''}>{p2?.name}</span>
    },
  },
  {
    key: 'player2Score',
    name: 'Score',
    minWidth: 50,
    maxWidth: 50,
    onRender: (item) => <span>{item?.players[1]?.score}</span>,
  },
  {
    key: 'restreamer',
    name: 'Restreamer/Location',
    minWidth: 150,
    maxWidth: 200,
    fieldName: 'restreamer_location',
  },
  {
    key: 'matchType',
    name: 'Type',
    minWidth: 60,
    maxWidth: 80,
    fieldName: 'match_type',
  },
]

const ALL_KEY = '__ALL__'
const ALL_OPTION = { key: ALL_KEY, text: 'ALL' }

const Tournament = () => {
  const dispatch = useDispatch()
  const matches = useSelector(selectTournamentMatches)
  const isLoading = useSelector(selectTournamentLoading)
  const events = useSelector(selectEvents)
  const editions = useSelector(selectEditions)
  const rounds = useSelector(selectRounds)

  const [event, setEvent] = useState(DEFAULT_EVENT)
  const [edition, setEdition] = useState(ALL_KEY)
  const [round, setRound] = useState(ALL_KEY)
  const [sortOrder, setSortOrder] = useState('desc')

  const eventComboRef = useRef(null)

  // Build options for dropdowns
  const eventOptions = events.map((e) => ({ key: e, text: e }))
  const editionOptions = [ALL_OPTION, ...editions.map((e) => ({ key: e, text: e }))]
  const roundOptions = [ALL_OPTION, ...rounds.map((r) => ({ key: r, text: r }))]

  // Fetch matches when filters change
  useEffect(() => {
    const params = { sort_order: sortOrder }
    if (event) params.event = event
    if (edition !== ALL_KEY) params.edition = edition
    if (round !== ALL_KEY) params.round_game = round

    dispatch(getTournamentMatches(params))
  }, [event, edition, round, sortOrder, dispatch])

  // Load initial filter options
  useEffect(() => {
    dispatch(getEvents())
    dispatch(getEditions(DEFAULT_EVENT))
    dispatch(getRounds(DEFAULT_EVENT))
  }, [dispatch])

  // When event changes, reload editions and rounds
  const handleEventSelect = (_, option) => {
    if (!option) return
    setEvent(option.key)
    setEdition(ALL_KEY)
    setRound(ALL_KEY)
    dispatch(getEditions(option.key))
    dispatch(getRounds(option.key))
  }

  const handleEditionSelect = (_, option) => {
    if (!option) return
    setEdition(option.key)
    setRound(ALL_KEY)
    const editionParam = option.key === ALL_KEY ? undefined : option.key
    dispatch(getRounds(event, editionParam))
  }

  const handleRoundSelect = (_, option) => {
    if (!option) return
    setRound(option.key)
  }

  const fetchEvents = useCallback(
    debounce((filter) => {
      dispatch(getEvents(filter))
    }, 300),
    [dispatch]
  )

  useEffect(() => () => fetchEvents.cancel(), [fetchEvents])

  const handleEventInput = (text) => {
    fetchEvents(text)
    if (text) eventComboRef.current?.focus(true)
  }

  const toggleSort = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))
  }

  return (
    <div className="tournament">
      <div className="filterBar">
        <ComboBox
          componentRef={eventComboRef}
          label="Event"
          placeholder="Search events..."
          allowFreeform
          autoComplete="off"
          openMenuOnClick
          selectedKey={event}
          options={eventOptions}
          onChange={handleEventSelect}
          onInputValueChange={handleEventInput}
          styles={{ root: { minWidth: 220 } }}
        />

        <ComboBox
          label="Edition"
          selectedKey={edition}
          options={editionOptions}
          onChange={handleEditionSelect}
          openMenuOnClick
          autoComplete="off"
          styles={{
            root: { minWidth: 160 },
            optionsContainerWrapper: { minWidth: 200 },
          }}
        />

        <ComboBox
          label="Round / Division"
          selectedKey={round}
          options={roundOptions}
          onChange={handleRoundSelect}
          openMenuOnClick
          autoComplete="off"
          styles={{ root: { minWidth: 160 } }}
        />

        <div className="sortToggle">
          <span className="sortLabel">Sort</span>
          <IconButton
            iconProps={{ iconName: sortOrder === 'desc' ? 'SortDown' : 'SortUp' }}
            title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            onClick={toggleSort}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="loadingState">
          <Spinner size={SpinnerSize.large} label="Loading matches..." />
        </div>
      ) : (
        <div className="matchTable" data-is-scrollable={true}>
          <div style={{ minWidth: 1100 }}>
            <DetailsList
              items={matches}
              columns={matchColumns}
              getKey={(item) => item.id}
              setKey="none"
              selectionMode="none"
              checkboxVisibility={2}
            />
          </div>
          {matches.length === 0 && (
            <div className="emptyState">No matches found for the selected filters.</div>
          )}
        </div>
      )}
    </div>
  )
}

export default Tournament
